import React from "react";
import styled from "styled-components";
import { startCase } from "lodash";
import { Link } from "@sonder/ui/source/styled/fragments/Link";
import { PanelCard } from "@sonder/ui/source/styled/fragments/PanelCard";
import { PanelCardProperty } from "@sonder/ui/source/styled/fragments/PanelCardProperty";
import { PanelCardSectionHeader } from "@sonder/ui/source/styled/fragments/PanelCardSectionHeader";
import { colors } from "@sonder/ui/source/styled/theme";
import { useSegment } from "analytics/segment";
import { useStore as useAuthStore } from "auth/store";
import { TaskData } from "core/queries/GetTask";
import { useUpdateTask } from "core/queries/UpdateTask";
import { getCleaningRequestUrl } from "core/queries/mappers";
import { CleaningCompanyPicker } from "tasks/components/CleaningCompanyPicker";
import { ParsedText } from "tasks/components/ParsedText";

const Disclaimer = styled.div`
  color: ${colors.gray6};
  font-size: 0.857rem;
  margin-top: 0.5rem;
`;

export const CleaningInfoCard = ({ task }: { task: TaskData }) => {
  const { track } = useSegment();
  const { state: authState } = useAuthStore();
  const [onUpdateTask, { loading: isUpdating }] = useUpdateTask({
    refetchQueries: [
      // Top Level Metrics for /schedule
      "GetSchedulerMetrics",
      // List of Cleans for /schedule
      "GetTaskSchedules",
      // List of Tasks for /tasks
      "GetTasks",
      // Task Details Panel
      "GetTask",
    ],
  });
  const { cleaningRequest } = task;

  if (!cleaningRequest) return null;

  const { category, cleaning, company } = cleaningRequest;
  const noteFromAdmin = cleaning?.noteFromAdmin || null;
  const noteFromCompany = cleaning?.noteFromCompany || null;

  const cleaningRequestUrl = getCleaningRequestUrl(task, authState?.user);
  const isCleaningScheduled = task.scheduledStartAt || task.dueAt;

  return (
    <PanelCard>
      <PanelCardSectionHeader>Cleaning</PanelCardSectionHeader>
      <PanelCardProperty
        label="Company"
        value={
          <>
            <CleaningCompanyPicker
              disabled={isUpdating || isCleaningScheduled}
              value={company}
              onChange={newCompany =>
                onUpdateTask({
                  variables: {
                    taskId: task.id,
                    newCleaningCompanyId: newCompany.id,
                  },
                })
              }
            />
            {isCleaningScheduled && (
              <Disclaimer>
                Unschedule the cleaning to be able to reassign.
              </Disclaimer>
            )}
          </>
        }
      />
      <PanelCardProperty
        label="Category"
        value={category ? startCase(category) : "---"}
      />
      {noteFromAdmin && (
        <PanelCardProperty
          label="Admin Notes"
          value={<ParsedText>{noteFromAdmin}</ParsedText>}
        />
      )}
      {noteFromCompany && (
        <PanelCardProperty
          label="Company Notes"
          value={<ParsedText>{noteFromCompany}</ParsedText>}
        />
      )}
      {cleaningRequestUrl && (
        <PanelCardProperty
          label="Legacy URL"
          value={
            <Link
              href={cleaningRequestUrl}
              target="_blank"
              onClick={() =>
                track({
                  action: "click",
                  element: "task_legacy_cleaning_link",
                  eventName: "task_panel",
                  props: { taskId: task.id },
                })
              }
            >
              {cleaningRequestUrl}
            </Link>
          }
        />
      )}
    </PanelCard>
  );
};
